'use client'

import { useTranslations } from 'next-intl'
import { ReactNode, useRef } from 'react'

import modalStore from '../../stores/modal-store'
import { Cross1Icon } from '@radix-ui/react-icons'
import { Dialog, Flex, IconButton, VisuallyHidden } from '@radix-ui/themes'

import { StyledContent } from '../styled'

const { Root, Title, Description } = Dialog

const GlobalModal = () => {
    const { useStore, resetModal } = modalStore
    const contents = useStore()

    return (
        <>
            {contents.map(({ key, ...content }, index) => (
                <Root
                    key={key}
                    open
                    onOpenChange={open => {
                        content.onOpenChange?.(open)
                        if (!open)
                            resetModal(index)
                    }}
                >
                    <GlobalModalContent {...content}>{content.content}</GlobalModalContent>
                </Root>
            ))}
        </>
    )
}

export default GlobalModal

type TModalContentProps = {
    title?: string
    titleHTML?: React.ReactNode
    description?: string
    width?: string
    height?: string
    preventOutsideClose?: boolean
    children: ReactNode
}

export const GlobalModalContent = ({
    title,
    titleHTML,
    description,
    width = '30rem',
    height,
    preventOutsideClose = false,
    children,
}: TModalContentProps) => {
    const t = useTranslations('modal')
    // Store-driven modals have no Dialog.Trigger, so Radix would move focus to <body> on close;
    // remember what was focused on open and return focus there instead.
    const returnFocusRef = useRef<HTMLElement | null>(null)

    return (
        <StyledContent
            $width={width}
            $height={height}
            onPointerDownOutside={event => {
                const target = event.target as Element | null
                // prevent closing when clicking on tinyMCE elements
                if (preventOutsideClose || target?.closest('.tox'))
                    event.preventDefault()
            }}
            onEscapeKeyDown={event => {
                if (preventOutsideClose)
                    event.preventDefault()
            }}
            onOpenAutoFocus={() => {
                if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body)
                    returnFocusRef.current = document.activeElement
            }}
            onCloseAutoFocus={event => {
                event.preventDefault()
                // a modal opened from a DropdownMenu can leave `pointer-events: none` on body
                document.body.style.pointerEvents = ''

                const target = returnFocusRef.current
                if (target?.isConnected)
                    target.focus()
            }}
        >
            <Flex direction="column">
                <Flex justify="between">
                    {title !== undefined && <Title>{title}</Title>}
                    {titleHTML !== undefined && titleHTML}
                    {/* Radix requires a Dialog.Title; a custom `titleHTML` header gets a hidden one */}
                    {title === undefined && (titleHTML !== undefined ? (
                        <VisuallyHidden>
                            <Title />
                        </VisuallyHidden>
                    ) : (
                        <Title />
                    ))}
                    <Dialog.Close>
                        <IconButton variant="ghost" aria-label={t('close')}>
                            <Cross1Icon />
                        </IconButton>
                    </Dialog.Close>
                </Flex>
                <Description>{description}</Description>
            </Flex>
            {children}
        </StyledContent>
    )
}
